// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Tooltip } from '@mantine/core';
import type { Cms1500Form } from '@polarissleep/shared';
import { CMS1500_STATES } from '@polarissleep/shared';
import { IconChevronDown, IconSearch } from '@tabler/icons-react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import cx from './Cms1500Form.module.css';

export { cx };

export interface Cms1500EditorContextValue {
  form: Cms1500Form;
  readOnly: boolean;
  /** Set one field by dot path (`ins.dob`, `lines.0.mods.1`). */
  setValue: (path: string, value: unknown) => void;
  /** Apply a structural change (add a line, remove an attachment) to a copy of the form. */
  update: (mutate: (draft: Cms1500Form) => void) => void;
  /** The field's validation message, once it has been touched or validation was requested. */
  errorFor: (path: string) => string | undefined;
  touch: (path: string) => void;
}

export const Cms1500EditorContext = createContext<Cms1500EditorContextValue | undefined>(undefined);

export function useCms1500Editor(): Cms1500EditorContextValue {
  const value = useContext(Cms1500EditorContext);
  if (!value) {
    throw new Error('useCms1500Editor must be used inside Cms1500EditorContext');
  }
  return value;
}

export function getPath(form: Cms1500Form, path: string): unknown {
  let current: unknown = form;
  for (const part of path.split('.')) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function setPath(form: Cms1500Form, path: string, value: unknown): void {
  const parts = path.split('.');
  const last = parts.pop() as string;
  let target = form as unknown as Record<string, unknown>;
  for (const part of parts) {
    target = target[part] as Record<string, unknown>;
  }
  target[last] = value;
}

export const classes = (...names: (string | false | undefined)[]): string => names.filter(Boolean).join(' ');

/** Grid column count for a `.row` or `.grid` (`--c` / `--g`). */
export const cols = (variable: '--c' | '--g', count: number): CSSProperties => ({ [variable]: count }) as CSSProperties;

export function Req({ show }: { show?: boolean }): JSX.Element | null {
  return show ? <i className={cx.req}>*</i> : null;
}

/** Black CMS-1500 box badge (`1`, `11a`, `19b`). */
export function BoxNum({ n }: { n: string }): JSX.Element {
  return <span className={classes(cx.num, n.length > 3 && cx.numWide)}>{n}</span>;
}

/** Cell heading: box number, title, required asterisk, `?` help and an optional trailing action. */
export function Head({
  n,
  title,
  req,
  help,
  extra,
}: {
  n?: string;
  title: ReactNode;
  req?: boolean;
  help?: string;
  extra?: ReactNode;
}): JSX.Element {
  return (
    <div className={cx.head}>
      {n && <BoxNum n={n} />}
      <span className={cx.title}>
        {title}
        <Req show={req} />
      </span>
      {extra}
      {help && (
        <Tooltip label={help} multiline w={280} withArrow>
          <span className={cx.help} aria-label={help}>
            ?
          </span>
        </Tooltip>
      )}
    </div>
  );
}

/** A field with a small label above it (`Last`, `0B State License Number`). */
export function Field({
  label,
  code,
  req,
  children,
}: {
  label?: ReactNode;
  code?: string;
  req?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={cx.field}>
      {label !== undefined && (
        <label className={cx.label}>
          {code && <b>{code}</b>}
          {label}
          <Req show={req} />
        </label>
      )}
      {children}
    </div>
  );
}

function ErrorText({ path }: { path: string }): JSX.Element | null {
  const { errorFor } = useCms1500Editor();
  const message = errorFor(path);
  return message ? <div className={cx.error}>{message}</div> : null;
}

interface InputProps {
  path: string;
  placeholder?: string;
  maxLength?: number;
  type?: string;
  uppercase?: boolean;
  readOnly?: boolean;
  white?: boolean;
  label?: string;
}

function TextControl({
  path,
  placeholder,
  maxLength,
  type,
  uppercase,
  readOnly,
  white,
  label,
}: InputProps): JSX.Element {
  const { form, setValue, errorFor, touch, readOnly: formReadOnly } = useCms1500Editor();
  return (
    <input
      className={classes(cx.input, white && cx.white, errorFor(path) && cx.bad)}
      type={type ?? 'text'}
      value={String(getPath(form, path) ?? '')}
      placeholder={placeholder}
      maxLength={maxLength}
      readOnly={readOnly}
      disabled={formReadOnly}
      aria-label={label}
      aria-invalid={Boolean(errorFor(path))}
      autoComplete="off"
      data-claim-field={path}
      onChange={(event) => setValue(path, event.currentTarget.value)}
      onBlur={(event) => {
        if (uppercase && event.currentTarget.value !== event.currentTarget.value.toUpperCase()) {
          setValue(path, event.currentTarget.value.toUpperCase());
        }
        touch(path);
      }}
    />
  );
}

export function TextIn(props: InputProps): JSX.Element {
  return (
    <>
      <TextControl {...props} />
      <ErrorText path={props.path} />
    </>
  );
}

export function DateIn({ path, label }: { path: string; label?: string }): JSX.Element {
  return <TextIn path={path} type="date" label={label} />;
}

export function MoneyIn({ path, label }: { path: string; label?: string }): JSX.Element {
  return (
    <>
      <div className={cx.money}>
        <span>$</span>
        <TextControl path={path} placeholder="0.00" label={label} />
      </div>
      <ErrorText path={path} />
    </>
  );
}

export function SearchIn(props: InputProps): JSX.Element {
  return (
    <>
      <div className={cx.search}>
        <IconSearch />
        <TextControl {...props} />
      </div>
      <ErrorText path={props.path} />
    </>
  );
}

export function SelectIn({
  path,
  options,
  placeholder = '',
  label,
}: {
  path: string;
  options: [string, string][] | string[];
  placeholder?: string;
  label?: string;
}): JSX.Element {
  const { form, setValue, errorFor, touch, readOnly } = useCms1500Editor();
  const entries = options.map((option) => (typeof option === 'string' ? [option, option] : option));
  return (
    <>
      <div className={cx.selectWrap}>
        <select
          className={classes(cx.input, errorFor(path) && cx.bad)}
          value={String(getPath(form, path) ?? '')}
          disabled={readOnly}
          aria-label={label}
          aria-invalid={Boolean(errorFor(path))}
          data-claim-field={path}
          onChange={(event) => {
            setValue(path, event.currentTarget.value);
            touch(path);
          }}
          onBlur={() => touch(path)}
        >
          <option value="">{placeholder}</option>
          {entries.map(([value, text]) => (
            <option key={value} value={value}>
              {text}
            </option>
          ))}
        </select>
        <IconChevronDown />
      </div>
      <ErrorText path={path} />
    </>
  );
}

export function Radios({
  path,
  options,
  stack,
  label,
}: {
  path: string;
  options: [string, string][];
  stack?: boolean;
  label?: string;
}): JSX.Element {
  const { form, setValue, errorFor, touch, readOnly } = useCms1500Editor();
  const value = String(getPath(form, path) ?? '');
  return (
    <>
      <div
        className={classes(cx.radios, stack && cx.stack, errorFor(path) && cx.radiosBad)}
        role="radiogroup"
        aria-label={label}
        data-claim-field={path}
      >
        {options.map(([option, text]) => (
          <label key={option} className={cx.radio}>
            <input
              type="radio"
              name={`cx-${path}`}
              value={option}
              checked={value === option}
              disabled={readOnly}
              onChange={() => {
                setValue(path, option);
                touch(path);
              }}
            />
            <span>{text}</span>
          </label>
        ))}
      </div>
      <ErrorText path={path} />
    </>
  );
}

const YES_NO: [string, string][] = [
  ['yes', 'Yes'],
  ['no', 'No'],
];

export function YesNo({ path, label }: { path: string; label?: string }): JSX.Element {
  return <Radios path={path} options={YES_NO} label={label} />;
}

export const SEX_OPTIONS: [string, string][] = [
  ['M', 'Male'],
  ['F', 'Female'],
  ['U', 'Unknown'],
];

/** Last / Suffix / First / Middle, as Stedi's 1500 lays a name out. */
export function NameFields({
  prefix,
  requireLast,
  requireFirst,
}: {
  prefix: string;
  requireLast?: boolean;
  requireFirst?: boolean;
}): JSX.Element {
  return (
    <div className={classes(cx.grid, cx.name)}>
      <Field label="Last" req={requireLast}>
        <TextIn path={`${prefix}.last`} maxLength={60} label="Last name" />
      </Field>
      <Field label="Suffix">
        <TextIn path={`${prefix}.suffix`} maxLength={10} label="Suffix" />
      </Field>
      <Field label="First" req={requireFirst}>
        <TextIn path={`${prefix}.first`} maxLength={35} label="First name" />
      </Field>
      <Field label="Middle">
        <TextIn path={`${prefix}.middle`} maxLength={25} label="Middle name" />
      </Field>
    </div>
  );
}

export function AddressFields({ prefix, required }: { prefix: string; required?: boolean }): JSX.Element {
  return (
    <>
      <Field label="Line 1" req={required}>
        <SearchIn path={`${prefix}.line1`} maxLength={55} label="Address line 1" />
      </Field>
      <Field label="Line 2">
        <TextIn path={`${prefix}.line2`} maxLength={55} label="Address line 2" />
      </Field>
      <Field label="City" req={required}>
        <TextIn path={`${prefix}.city`} maxLength={30} label="City" />
      </Field>
      <div className={cx.grid}>
        <Field label="State" req={required}>
          <SelectIn path={`${prefix}.state`} options={CMS1500_STATES} label="State" />
        </Field>
        <Field label="ZIP Code" req={required}>
          <TextIn path={`${prefix}.zip`} maxLength={10} label="ZIP Code" />
        </Field>
      </div>
    </>
  );
}
